import React, { useState } from 'react';

import 'swiper/css';
import 'swiper/css/bundle';

import { Button, ButtonTypeMap, Card, styled } from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';

import CardSlide from '../core/card-slide';
import { ColorVariantsSlider } from './color-variants-slider';
import styles from './product-card-color-slider.module.scss';

export type ProductCardSlideProps = React.ComponentProps<typeof Card> & {
  title?: string;
  subtitle?: string;
  variants: Variant[];
  className?: string;
  ImageLinkComponent?: React.ElementType;
  protectiveIcon?: React.ReactNode;
};

export type Variant = {
  image: React.ReactElement<HTMLElement>;
  colorName: string;
  footerButtonProps: OverrideProps<ButtonTypeMap, React.ElementType>;
  imageLinkProps?: unknown;
};

export const ProductCardColorSlider = ({
  title,
  subtitle,
  variants,
  className = '',
  ImageLinkComponent,
  protectiveIcon,
}: ProductCardSlideProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const VariantLink = ImageLinkComponent || 'a';

  const productLinkArea = (
    <>
      <div className={styles.mainSwiper}>
        {/* NOTE: Here we render all variant images with an absolute position
        and just fade in the active one. This is not really a good solution,
        but seems to be more performant that using the Swiper with a fade
        transition. Ideally we don't use a Swiper but still just render the
        images which are needed to perform the transition instead of all the
        variant images.*/}
        {variants.map((v, idx) => (
          <div
            key={idx}
            className={styles.imageWrapper}
            style={{
              opacity: idx === selectedIndex ? 1 : 0,

              // Put active image on top to make sure "open image in new tab"
              // is still working correctly.
              zIndex: idx === selectedIndex ? 1 : 0,

              // Don't display inactive images to exclude them from the layout
              // calculation and increase rendering performance.
              display: Math.abs(idx - selectedIndex) > 1 ? 'none' : 'block',
            }}
          >
            {React.cloneElement(v.image, {
              className: styles.mainImage,
            })}
          </div>
        ))}
      </div>
      <div className={styles.productNameContainer}>
        <ProductName>{title}</ProductName>
        <CollectionName>{subtitle}</CollectionName>
      </div>
    </>
  );

  const hasProductDetails = variants.every((v) => !!v.imageLinkProps);

  return (
    <CardSlide className={className}>
      <Card variant="outlined" className={styles.productCard}>
        {hasProductDetails ? (
          <VariantLink
            {...(variants[selectedIndex].imageLinkProps ?? {})}
            className={styles.variantLink}
          >
            <div className={styles.productLinkArea}>{productLinkArea}</div>
          </VariantLink>
        ) : (
          <div className={styles.productLinkArea}>{productLinkArea}</div>
        )}
        <ColorVariantsSlider
          variants={variants}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
        {hasProductDetails && (
          <div className={styles.buttonContainer}>
            <Button
              variant="text"
              className={styles.showDetails}
              {...variants[selectedIndex]?.footerButtonProps}
            />
          </div>
        )}
        {protectiveIcon && <IconWrapper>{protectiveIcon}</IconWrapper>}
      </Card>
    </CardSlide>
  );
};
ProductCardColorSlider.displayName = 'SwiperSlide';

const IconWrapper = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 'var(--spacer-s)',
  right: 'var(--spacer-s)',
  zIndex: 1,
  pointerEvents: 'none',

  svg: {
    height: '60px',
    width: '60px',
    color: 'var(--color-dark-coal)',

    [theme.breakpoints.up('md')]: {
      height: '75px',
      width: '75px',
    },

    [theme.breakpoints.up('lg')]: {
      height: '100px',
      width: '100px',
    },
  },
}));

const ProductName = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  textAlign: 'center',
  margin: '0',
}));

const CollectionName = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightRegular,
  opacity: '0.7',
  textAlign: 'center',
  margin: '0',
}));
